import { MarkdownCitationChip } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownCitationChip";
import {
  MarkdownResponseContext,
  useMarkdownResponseContext,
} from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownContext";
import {
  CitationPlugin,
  LineBreakPlugin,
  MediaPlugin,
  parseCitationLink,
  ResultsPlugin,
} from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownPlugin";
import Sentry from "@mirage/shared/sentry";
import classNames from "classnames";
import { memo } from "react";
import type { Options as ReactMarkdownOptions } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "./MarkdownResponse.module.css";

import { PAPEvent } from "@mirage/analytics/events/base/event";
import { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";

// don't allow images in markdown.
const disallowedElements = ["img"];

export interface MarkdownResponseProps {
  className?: string;
  markdownText: string;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  referencingSources?: ChatSource[];
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;

  // react-markdown props
  remarkPlugins?: ReactMarkdownOptions["remarkPlugins"];
  components?: ReactMarkdownOptions["components"];
}
export const MarkdownResponse = memo(
  ({
    className,
    markdownText,
    logComposeEvent,
    referencingSources,
    conversationId,
    searchSessionId,
    assistRequestId,
    remarkPlugins,
    components,
  }: MarkdownResponseProps) => {
    const isSearchPageDataRetrieval = useFeatureFlagValueBool(
      "dash_2025_05_21_serp_data_retrieval",
    );
    return (
      <div className={classNames(styles.MarkdownResponse, className)}>
        <MarkdownResponseContext.Provider
          value={{
            referencingSources,
            conversationId,
            logComposeEvent,
            searchSessionId,
            assistRequestId,
          }}
        >
          <ReactMarkdown
            remarkPlugins={[
              remarkGfm,
              LineBreakPlugin,
              CitationPlugin,
              ...(isSearchPageDataRetrieval
                ? [ResultsPlugin, MediaPlugin]
                : []),
              ...(remarkPlugins || []),
            ]}
            disallowedElements={disallowedElements}
            components={{
              a: Link,
              ...(components || {}),
            }}
          >
            {markdownText}
          </ReactMarkdown>
        </MarkdownResponseContext.Provider>
      </div>
    );
  },
);
MarkdownResponse.displayName = "MarkdownResponse";

const Link = memo((props: { href: string; children: React.ReactNode }) => {
  const {
    referencingSources,
    conversationId,
    logComposeEvent,
    assistRequestId,
    searchSessionId,
  } = useMarkdownResponseContext();
  const { children, href, ...rest } = props;

  const sourceIndex = parseCitationLink(href);
  if (sourceIndex !== undefined) {
    const source = referencingSources?.find(
      (source) => source.index === sourceIndex,
    );
    if (source !== undefined) {
      return (
        <MarkdownCitationChip
          source={source}
          conversationId={conversationId}
          logComposeEvent={logComposeEvent}
          assistRequestId={assistRequestId}
          searchSessionId={searchSessionId}
        />
      );
    } else {
      // Ignore citation links that were hallucinated by the AI
      Sentry.withScope((scope) => {
        scope.setTag("sourceIndex", sourceIndex);
        Sentry.captureMessage(
          "[Assistant] citation link with invalid index",
          "error",
          {},
          scope,
        );
      });
      return null;
    }
  }

  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
});
Link.displayName = "Link";
